import { z } from 'zod';
import { cleanString } from './clean.js';
import {
  CompanyType,
  DocumentKind,
  DocumentScope,
  DocumentUploadedBy,
  EngagementStatus,
} from './onboarding-enums.js';

// ── Picker (T-062) ───────────────────────────────────────────────────
// Minimal vendor shape for the "add resource to an existing vendor" picker in
// the New Onboarding dialog. Non-sensitive fields only. Parses the richer list
// response too (extra keys are stripped).
export const VendorPickerItem = z.object({
  id: z.string().uuid(),
  business_name: z.string(),
  business_address_city: z.string().nullable(),
  business_address_state: z.string().nullable(),
});
export type VendorPickerItem = z.infer<typeof VendorPickerItem>;

export const VendorPickerResponse = z.object({
  items: z.array(VendorPickerItem),
});
export type VendorPickerResponse = z.infer<typeof VendorPickerResponse>;

// ── List (T-063) ─────────────────────────────────────────────────────
// One row per vendor (the business). ACH/EIN (encrypted) are NEVER included.
export const VendorListItem = z.object({
  id: z.string().uuid(),
  business_name: z.string(),
  // Nullable since stub-backfilled vendors carry only the core fields (T-049).
  company_type: CompanyType.nullable(),
  business_address_city: z.string().nullable(),
  business_address_state: z.string().nullable(),
  resource_count: z.number().int().nonnegative(),
  is_referrer: z.boolean(),
  /** True once the vendor has a stored QuickBooks vendor id (T-205). */
  qb_linked: z.boolean(),
  created_at: z.string(),
});
export type VendorListItem = z.infer<typeof VendorListItem>;

export const VendorListResponse = z.object({
  items: z.array(VendorListItem),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
});
export type VendorListResponse = z.infer<typeof VendorListResponse>;

/**
 * Derived completeness (mirrors the old contractor badge): a stub-backfilled
 * vendor carries only its core fields. "Complete" means the business identity
 * fields are present. No stored flag.
 */
export type VendorCompletenessFields = Pick<
  VendorListItem,
  'company_type' | 'business_address_city' | 'business_address_state'
>;

export function isVendorComplete(v: VendorCompletenessFields): boolean {
  return (
    !!v.company_type &&
    !!v.business_address_city?.trim() &&
    !!v.business_address_state?.trim()
  );
}

// ── Detail (T-063) ───────────────────────────────────────────────────
export const VendorDocument = z.object({
  id: z.string().uuid(),
  kind: DocumentKind,
  scope: DocumentScope,
  original_filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int().nonnegative(),
  uploaded_by: DocumentUploadedBy,
  created_at: z.string(),
  download_url: z.string(),
});
export type VendorDocument = z.infer<typeof VendorDocument>;

// rate_to_client is OMITTED (not nulled) for every non-owner (T-186) — one
// schema parses both shapes.
export const VendorEngagement = z.object({
  id: z.string().uuid(),
  client_name: z.string(),
  rate_to_contractor: z.number().nullable(),
  rate_to_client: z.number().nullable().optional(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  product_service: z.string(),
  status: EngagementStatus,
});
export type VendorEngagement = z.infer<typeof VendorEngagement>;

// A person under the vendor, with their engagements + resource-scope documents.
export const VendorResource = z.object({
  id: z.string().uuid(),
  resource_name: z.string(),
  resource_email: z.string().nullable(),
  role: z.string().nullable(),
  worker_id: z.string().nullable(),
  mobile_phone: z.string().nullable(),
  resource_mailing_address_street: z.string().nullable(),
  resource_mailing_address_city: z.string().nullable(),
  resource_mailing_address_state: z.string().nullable(),
  resource_mailing_address_zip: z.string().nullable(),
  engagements: z.array(VendorEngagement),
  documents: z.array(VendorDocument),
});
export type VendorResource = z.infer<typeof VendorResource>;

export const VendorDetail = z.object({
  id: z.string().uuid(),
  business_name: z.string(),
  business_email: z.string().nullable(),
  company_type: CompanyType.nullable(),
  mobile_phone: z.string().nullable(),
  office_phone: z.string().nullable(),
  state_of_incorporation: z.string().nullable(),
  business_address_street: z.string().nullable(),
  business_address_city: z.string().nullable(),
  business_address_state: z.string().nullable(),
  business_address_zip: z.string().nullable(),
  // Sensitive fields are MASKED — only last-4 ever leaves the server.
  ach_routing_masked: z.string().nullable(),
  ach_account_masked: z.string().nullable(),
  ein_masked: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Business-scope documents (owned by the vendor).
  documents: z.array(VendorDocument),
  resources: z.array(VendorResource),
});
export type VendorDetail = z.infer<typeof VendorDetail>;

// ── Referrer flag (T-127) ────────────────────────────────────────────
// Curated referrer list: add = flag, remove = un-flag (keeps history — an
// engagement already referred by the vendor stays valid; the vendor just
// drops out of the onboarding picker).
export const VendorReferrerUpdateInput = z.object({
  is_referrer: z.boolean(),
});
export type VendorReferrerUpdateInput = z.infer<typeof VendorReferrerUpdateInput>;

// ── Full ACH reveal (T-077) ──────────────────────────────────────────
// Admin-only, on-demand: the unmasked routing + account so an admin can enter
// them into QuickBooks Bill Pay. Plaintext only travels on this explicit request.
export const VendorAchResponse = z.object({
  ach_routing: z.string().nullable(),
  ach_account: z.string().nullable(),
});
export type VendorAchResponse = z.infer<typeof VendorAchResponse>;

// ── Manual create (T-199) ────────────────────────────────────────────
// Admin-entered vendor with no resource, engagement, onboarding, or
// QuickBooks side effects — the H-1B vendor-switch case, where the new vendor
// exists only in Eric's QuickBooks and must be introduced to the portal before
// an engagement can point at it (feeds T-172). Only the business name is
// required; the rest mirrors the optional business identity fields a
// stub-backfilled vendor carries, so a manually created vendor and a
// backfilled one are the same shape of incomplete.
//
// ACH/EIN are deliberately absent: they are encrypted-at-rest fields captured
// on the contractor's own onboarding form, not something an admin types in.
//
// An untouched input in the dialog submits '' — and the form resolver runs
// before any client-side tidying — so every optional field has to treat blank
// as "not provided" in the schema itself, or leaving a field alone would fail
// validation (an enum and an email both reject ''). Blank and
// whitespace-only alike land as null, matching a stub-backfilled row.
const optionalText = (max: number) =>
  cleanString(z.string().max(max))
    .nullable()
    .optional()
    .transform((v) => (v == null || v === '' ? null : v));

export const VendorCreateInput = z.object({
  business_name: cleanString(z.string().min(1).max(200)),
  business_email: optionalText(200).pipe(z.string().email().nullable()),
  company_type: z
    .union([CompanyType, z.literal(''), z.null()])
    .optional()
    .transform((v) => (v == null || v === '' ? null : v)),
  mobile_phone: optionalText(40),
  office_phone: optionalText(40),
  // Widths match the backfill row (the other bulk intake path), which allows a
  // spelled-out state rather than only the 2-letter code.
  state_of_incorporation: optionalText(60),
  business_address_street: optionalText(300),
  business_address_city: optionalText(120),
  business_address_state: optionalText(60),
  business_address_zip: optionalText(20),
});
export type VendorCreateInput = z.infer<typeof VendorCreateInput>;
// What the dialog's fields actually hold before the schema coerces them: every
// optional field is still a possibly-blank string. The form is typed on this;
// the submit handler receives the parsed VendorCreateInput.
export type VendorCreateFormValues = z.input<typeof VendorCreateInput>;
